import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

export default class extends Controller {
  static get targets() {
    return [
      "form", "statusCheckbox", "statusTag", "statusIcon", "statusText",
      "activePeriodValue", "activePeriodIcon", "activePeriodText",
      "dateRangePickersContainer", "dateRangePicker", "startsAtInput",
      "notificationsValue", "notificationsIcon", "notificationsText",
      "sendAtValue", "sendAtText",
      "delayedDaysValue", "delayedDaysText",
      "minimumLeaseTermValue", "minimumLeaseTermText",
      "timezoneValue", "timezoneText",
      "paymentValue", "paymentMethodIdValue", "paymentIcon", "paymentText", "paymentPickList",
      "toast", "toastIcon", "toastTitle", "toastContent",
      "submitButton"
    ];
  }

  static get values() {
    return { refreshPaymentMethodsPath: String };
  }

  connect() {
    this.stopped = false;
    this.isActive = this.statusCheckboxTarget.checked;
    this.updateStatusUI();

    const selectedActivePeriod = this.activePeriodValueTarget.value || "run_continuously";
    this.activePeriod = {
      value: selectedActivePeriod,
      iconClass: selectedActivePeriod === "date_range" ? "icon-lasso-calendar" : "icon-lasso-update",
      displayText: selectedActivePeriod === "date_range" ? "Date range" : "Run continuously"
    };
    this.activePeriodValueTarget.value = this.activePeriod.value;
    this.renderActivePeriod();

    const selectedFrequency = this.notificationsValueTarget.value || "monthly";
    this.notificationsFrequency = {
      value: selectedFrequency,
      iconClass: selectedFrequency === "monthly" ? "icon-lasso-alert" : "icon-lasso-circle-close",
      displayText: selectedFrequency === "monthly" ? "Monthly" : "None"
    };
    this.notificationsValueTarget.value = this.notificationsFrequency.value;
    this.renderNotifications();

    this.sendAt = { value: this.sendAtValueTarget.value || "09:00:00", displayText: "09:00 am" };
    this.sendAtValueTarget.value = this.sendAt.value;
    this.renderSendAt();

    const selectedSendAtTimezone = this.timezoneValueTarget.value || "Eastern Time (US & Canada)";
    this.sendAtTimezone = { value: selectedSendAtTimezone, displayText: selectedSendAtTimezone };
    this.timezoneValueTarget.value = this.sendAtTimezone.value;
    this.renderTimezone();

    const selectedDelayedDays = this.delayedDaysValueTarget.value || 0;
    this.delayedDays = {
      value: selectedDelayedDays,
      displayText: selectedDelayedDays == 0 ? "Immediately" : `${selectedDelayedDays} days`
    };
    this.delayedDaysValueTarget.value = this.delayedDays.value;
    this.renderDelayedDays();

    // Only rendered for organizations with a yardi lease term, so the targets are absent elsewhere.
    if (this.hasMinimumLeaseTermValueTarget) {
      const selectedMinimumLeaseTerm = this.minimumLeaseTermValueTarget.value;
      this.minimumLeaseTerm = {
        value: selectedMinimumLeaseTerm,
        displayText: selectedMinimumLeaseTerm == 1 ? "1 Month" : `${selectedMinimumLeaseTerm} Months`
      };
      this.renderMinimumLeaseTerm();
    }

    const selectedPaymentMethod = this.paymentValueTarget.value || "lt_credits";
    const selectedPaymentMethodId = this.paymentMethodIdValueTarget.value;
    const selectedPaymentMethodLast4 = this.paymentMethodIdValueTarget.dataset.last4;
    this.paymentMethod = {
      id: selectedPaymentMethodId,
      iconClass: selectedPaymentMethod === "credit_card" ? "icon-lasso-card" : "icon-lasso-lt-credits",
      value: selectedPaymentMethod,
      displayText: selectedPaymentMethod === "credit_card" ? `Card **** ${selectedPaymentMethodLast4} on demand` : "LT Credits"
    };
    this.paymentValueTarget.value = this.paymentMethod.value;
    this.renderPaymentMethod();

    this.formStatus = {
      toastClass: "toast-success",
      toastIconClass: "icon-lasso-fill-check",
      toastTitle: "Automation ready!",
      toastContent: "No issues found."
    };
    this.renderFormStatus();
  }

  // Three timers outlive a torn-down mount otherwise: the datepicker debounce, the modal's minimum
  // display countdown and the redirect that follows it. The redirect is the one that matters - it
  // would navigate a visitor who has already gone somewhere else. The flag covers the rest of the
  // window, where the request is still in flight and its answer would arm those timers after the
  // teardown had already cleared them.
  disconnect() {
    this.stopped = true;
    clearTimeout(this.debounceTimer);
    clearTimeout(this.modalDismissTimer);
    clearTimeout(this.redirectTimer);
    this.debounceTimer = null;
    this.modalDismissTimer = null;
    this.redirectTimer = null;
  }

  updateStatus(event) {
    this.isActive = event.target.checked;
    this.updateStatusUI();
  }

  updateStatusUI() {
    this.statusTagTarget.classList.remove("tag-redeemed", "tag-canceled");
    this.statusTagTarget.classList.add(this.isActive ? "tag-redeemed" : "tag-canceled");
    this.statusIconTarget.className = this.isActive ? "icon-lasso-sparkle" : "icon-lasso-minus-circle";
    this.statusTextTarget.textContent = this.isActive ? "Active" : "Inactive";
  }

  setActivePeriod(event) {
    const { value, iconClass, displayText } = event.params;
    this.activePeriod = { value, iconClass, displayText };
    this.activePeriodValueTarget.value = value;
    this.renderActivePeriod();

    this.dateRangePickerTargets.forEach(input => { input.value = ""; });

    if (value === "date_range") {
      this.dateRangePickersContainerTarget.classList.remove("hidden");
      this.dateRangePickerTargets.forEach(input => { input.disabled = false; });
      this.setFormStatus("toast-danger", "icon-lasso-warning-fill-triangle", "Automation not ready!", "Please select a date range.");
    } else {
      this.dateRangePickersContainerTarget.classList.add("hidden");
      this.dateRangePickerTargets.forEach(input => { input.disabled = true; });
      this.resetFormStatus();
    }
  }

  renderActivePeriod() {
    this.activePeriodIconTarget.className = this.activePeriod.iconClass;
    this.activePeriodTextTarget.textContent = this.activePeriod.displayText;
  }

  setNotificationsFrequency(event) {
    const { value, iconClass, displayText } = event.params;
    this.notificationsFrequency = { value, iconClass, displayText };
    this.notificationsValueTarget.value = value;
    this.renderNotifications();
  }

  renderNotifications() {
    this.notificationsIconTarget.className = this.notificationsFrequency.iconClass;
    this.notificationsTextTarget.textContent = this.notificationsFrequency.displayText;
  }

  setSendAt(event) {
    const { value, displayText } = event.params;
    this.sendAt = { value, displayText };
    this.sendAtValueTarget.value = value;
    this.renderSendAt();
  }

  renderSendAt() {
    this.sendAtTextTarget.textContent = this.sendAt.displayText;
  }

  setTimezone(event) {
    const { value, displayText } = event.params;
    this.sendAtTimezone = { value, displayText };
    this.timezoneValueTarget.value = value;
    this.renderTimezone();
  }

  renderTimezone() {
    this.timezoneTextTarget.textContent = this.sendAtTimezone.displayText;
  }

  setDelayedDays(event) {
    const { value, displayText } = event.params;
    this.delayedDays = { value, displayText };
    this.delayedDaysValueTarget.value = value;
    this.renderDelayedDays();
  }

  renderDelayedDays() {
    this.delayedDaysTextTarget.textContent = this.delayedDays.displayText;
  }

  setMinimumLeaseTerm(event) {
    const { value, displayText } = event.params;
    this.minimumLeaseTerm = { value, displayText };
    this.minimumLeaseTermValueTarget.value = value;
    this.renderMinimumLeaseTerm();
  }

  renderMinimumLeaseTerm() {
    this.minimumLeaseTermTextTarget.textContent = this.minimumLeaseTerm.displayText;
  }

  setPaymentMethod(event) {
    const { value, iconClass, displayText } = event.params;
    const paymentMethodId = event.params.paymentMethodId || "";
    const expired = event.params.expired || false;

    if (expired) {
      this.paymentMethod = { id: null, iconClass, value, displayText };
      this.paymentMethodIdValueTarget.value = "";
      this.setFormStatus("toast-danger", "icon-lasso-warning-fill-triangle", "Credit card failed", "The credit card has expired. Please update your payment method.");
    } else {
      this.paymentMethod = { id: paymentMethodId, iconClass, value, displayText };
      this.paymentMethodIdValueTarget.value = paymentMethodId;
      this.validateForm();
    }

    this.paymentValueTarget.value = value;
    this.renderPaymentMethod();
  }

  renderPaymentMethod() {
    this.paymentIconTarget.className = this.paymentMethod.iconClass;
    this.paymentTextTarget.textContent = this.paymentMethod.displayText;
  }

  resetFormStatus() {
    this.setFormStatus("toast-success", "icon-lasso-fill-check", "Automation ready!", "No issues found.");
  }

  setFormStatus(toastClass, toastIconClass, toastTitle, toastContent) {
    this.formStatus = { toastClass, toastIconClass, toastTitle, toastContent };
    this.renderFormStatus();
  }

  renderFormStatus() {
    this.toastTarget.classList.remove("toast-success", "toast-danger");
    this.toastTarget.classList.add(this.formStatus.toastClass);
    this.toastIconTarget.className = this.formStatus.toastIconClass;
    this.toastTitleTarget.textContent = this.formStatus.toastTitle;
    this.toastContentTarget.textContent = this.formStatus.toastContent;
    this.submitButtonTarget.disabled = !this.validForm();
  }

  validForm() {
    return this.formStatus.toastClass === "toast-success";
  }

  // ng-blur in datepickers fired twice in quick succession; keep the same debounce.
  debounceValidate() {
    if (this.debounceTimer) { clearTimeout(this.debounceTimer); }
    this.debounceTimer = setTimeout(() => { this.validateForm(); }, 200);
  }

  validateForm() {
    if (this.activePeriod.value === "date_range") {
      if (this.startsAtInputTarget.value) {
        this.resetFormStatus();
      } else {
        this.setFormStatus("toast-danger", "icon-lasso-warning-fill-triangle", "Automation not ready!", "Please select a start date.");
      }
    } else {
      this.resetFormStatus();
    }
  }

  refreshPaymentMethods() {
    const options = RequestHelper.defaultRequestOptions();

    fetch(this.refreshPaymentMethodsPathValue, options)
      .then(response => {
        if (!response.ok) { throw new Error(`Request failed with status ${response.status}`); }
        return response.text();
      })
      // S1-innerhtml, accepted: `html` is our own server-rendered markup -
      // Subdomain::Automations::AdditionalSettingsController#refresh_payment_method_options
      // renders only the `subdomain/automations/additional_settings/payment_methods_options`
      // partial, which builds every element through `content_tag` (auto-escaping) and calls
      // `.html_safe` on the assembled tag structure itself, never on unescaped interpolated
      // data - no `raw`/`html_safe`/`!=` is applied to any value read from the request. Same
      // origin, no user-entered data reaches the DOM unescaped.
      .then(html => { this.paymentPickListTarget.innerHTML = html; })
      .catch(error => { console.error("Error refreshing payment methods:", error); });
  }

  submitForm(event) {
    event.preventDefault();

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/x-www-form-urlencoded" });
    options.body = new URLSearchParams(new FormData(this.formTarget)).toString();

    $("#creating-automation-modal").modal("show");

    const modalShownTime = Date.now();
    const minDisplayTime = 3000;

    fetch(this.formTarget.getAttribute("action"), options)
      .then(response => {
        if (!response.ok) { throw new Error(`Request failed with status ${response.status}`); }
        return response.json();
      })
      .then(data => {
        if (this.stopped) { return; }

        const elapsedTime = Date.now() - modalShownTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

        this.modalDismissTimer = setTimeout(() => {
          $("#creating-automation-modal").modal("hide");

          this.redirectTimer = setTimeout(() => {
            // Our own controller's JSON response, same origin as this page - asserted before
            // navigating per the S3 rule rather than trusting `data.redirect_url` outright. The
            // failing branch does not navigate; it reuses the form's own toast error UI.
            if (RequestHelper.isSameOriginUrl(data.redirect_url)) {
              window.location.href = data.redirect_url;
            } else {
              this.setFormStatus("toast-danger", "icon-lasso-warning-fill-triangle", "Automation not ready!", "Something went wrong. Please try again.");
            }
          }, 500);
        }, remainingTime);
      })
      .catch(error => {
        console.error("Error submitting form:", error);

        if (this.stopped) { return; }

        const elapsedTime = Date.now() - modalShownTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

        this.modalDismissTimer = setTimeout(() => {
          $("#creating-automation-modal").modal("hide");
        }, remainingTime);
      });
  }
}
